import { AuthorAvatar } from '~/components/Author'
import { StructuredSchema } from '~/components/StructuredSchema'
import { generatePath } from 'react-router'
import { routes } from '~/queries/routes'
import { useRootContext } from '~/hooks/useRootContext'

import { FlexContainer, FlexItem } from '~/components/Grid'
import { LinkedInTextLogoIcon } from '~/components/icons'
import { Link } from '@remix-run/react'
import { Button } from '~/components/general/Button'
import { Asset } from 'contentful'

type AuthorHeroProps = {
  fields: {
    name: string
    slug: string
    avatar?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined
    title?: string | undefined
    bio?: string | undefined
    twitter?: string | undefined
    facebook?: string | undefined
    linkedin?: string | undefined
  }
}

export const AuthorHero = ({ fields }: AuthorHeroProps) => {
  const {
    url: { origin },
  } = useRootContext()
  const { name, bio, avatar, linkedin } = fields

  return (
    <div className="bg-neutral-5 relative text-neutral-100 p-[calc(30px_+_var(--header-height))] max-md:text-center max-md:p-[calc(10px_+_var(--header-height))]">
      <FlexContainer justify="end">
        <FlexItem
          desktop={10}
          laptop={12}
          className="relative flex flex-col gap-6 max-xl:items-center max-xl:w-auto"
        >
          <StructuredSchema
            schema={{
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Career Advice',
                  item: `${origin}${generatePath(routes.careerAdvice)}`,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: name,
                },
              ],
            }}
          />

          <div className="w-full overflow-hidden text-neutral-80 p-0 m-0 lg:mb-17 flex max-lg:pb-5 max-md:pb-3">
            <li className="text-[19px] after:[background-size:6px_16px] after:bg-[url('/images/breadcrumb-chevron.svg')] after:invert-[90%] after:bg-no-repeat after:-translate-y-1/2  max-md:text-[16px] max-lg:text-[17px] last:after:hidden last:pr-0 after:top-1/2 after:absolute after:right-0 after:block after:text-neutral-40 after:content-[''] after:w-4 after:h-4 pr-4 block relative whitespace-nowrap overflow-hidden text-ellipsis text-neutral-80">
              <Link
                className="cursor-pointer"
                to={generatePath(routes.careerAdvice)}
                title="Career Advice"
              >
                Career Advice
              </Link>
            </li>
            <li className="text-[19px] after:[background-size:6px_16px] after:bg-[url('/images/breadcrumb-chevron.svg')] after:invert-[90%] after:bg-no-repeat after:-translate-y-1/2  max-md:text-[16px] max-lg:text-[17px] last:after:hidden last:pr-0 after:top-1/2 after:absolute after:right-0 after:block after:text-neutral-40 after:content-[''] after:w-4 after:h-4 pr-4 block relative whitespace-nowrap overflow-hidden text-ellipsis text-neutral-80">
              <span title={name}>{name}</span>
            </li>
          </div>
          <div>
            {avatar && <AuthorAvatar asset={avatar} size="M" style={{ display: 'inline-block' }} />}
            <h1 className="inline-block text-2xl">{name}</h1>
          </div>
          {bio && <div className="text-s text-neutral-100 max-md:pt-4 max-md:pb-18">{bio}</div>}
          <div>
            <div className="text-xs text-neutral-50 flex gap-2" style={{ alignItems: 'self-end' }}>
              Follow {name} on
              <Button
                iconRight={<LinkedInTextLogoIcon className={'size-[24px]'} />}
                type={'link'}
                color={'transparent'}
                href={linkedin}
                target="_blank"
                className={'!bg-blue-60 !size-[28px] !p-0 justify-center !rounded-[5px]'}
              />
            </div>
          </div>
        </FlexItem>
      </FlexContainer>
    </div>
  )
}
